/** SUNTRONIX ONE — Asset Age Centre (Phase 2/3/4).
 *  Ageing analytics + report, Purchase-Date coverage/import/invoice-AI, and the
 *  tenant Allocation Policy editor. Suntronix-internal + tenant admins.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BarChart3, FileSpreadsheet, CalendarClock, SlidersHorizontal, Download, Upload,
  Loader2, Sparkles, CheckCircle2, Building2,
} from "lucide-react";

const TABS = [
  { k: "overview", l: "Ageing Overview", icon: BarChart3 },
  { k: "report", l: "Ageing Report", icon: FileSpreadsheet },
  { k: "coverage", l: "Purchase Dates", icon: CalendarClock },
  { k: "policy", l: "Allocation Policy", icon: SlidersHorizontal },
];
const BUCKET_LABEL = { "0-1y": "0–1 yr", "1-2y": "1–2 yr", "2-3y": "2–3 yr", "3-4y": "3–4 yr", "4y+": "4+ yr", unknown: "Unknown" };
const REQS = ["PREMIUM", "STANDARD", "TRAINING_TEMP", "AGEING_STOCK", "NEW_STOCK", "REDEPLOYMENT", "NO_PREFERENCE"];
const PREF_OPTS = ["NEW_STOCK", "STANDARD", "AGEING_STOCK", "REDEPLOYMENT", "NO_PREFERENCE"];

export default function AssetAgeCentre() {
  const [tab, setTab] = useState("overview");
  const [mc, setMc] = useState("");
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get("/master-companies", { params: { limit: 200 } })
      .then(({ data }) => { const items = data.items || data || []; setCompanies(items); if (items[0]) setMc(items[0].id); })
      .catch(() => {});
  }, []);

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="asset-age-centre">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center"><CalendarClock className="h-6 w-6" strokeWidth={1.6} /></div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Asset Age Centre</h1>
          <p className="text-sm text-slate-500 mt-0.5">Ageing intelligence, purchase-date coverage and allocation policy.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <select data-testid="aac-tenant" value={mc} onChange={(e) => setMc(e.target.value)} className="h-9 rounded-lg border border-slate-300 text-[13px] px-2 max-w-[240px]">
            {companies.map((c) => <option key={c.id} value={c.id}>{orgLabel(c, companies)}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`aac-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-[13px] font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === t.k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="h-4 w-4" /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview mc={mc} />}
      {tab === "report" && <Report mc={mc} />}
      {tab === "coverage" && <Coverage mc={mc} />}
      {tab === "policy" && <Policy mc={mc} />}
    </div>
  );
}

function Overview({ mc }) {
  const [a, setA] = useState(null);
  useEffect(() => { if (!mc) return; api.get("/deployment/ageing-analytics", { params: { master_company_id: mc } }).then(({ data }) => setA(data)).catch(() => setA(null)); }, [mc]);
  if (!a) return <div className="py-10 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>;
  const max = Math.max(1, ...Object.values(a.buckets));
  return (
    <div className="space-y-4" data-testid="aac-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Total assets", a.total], ["Available to deploy", a.available_for_deployment], ["Never deployed", a.never_deployed], ["Redeployment ready", a.redeployment_ready]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-2xl font-bold text-slate-900">{v}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[12px] font-semibold text-slate-700 mb-3">Asset age distribution</div>
        <div className="space-y-2">
          {Object.entries(a.buckets).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <div className="w-20 text-[12px] text-slate-500">{BUCKET_LABEL[k]}</div>
              <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden">
                <div className="h-full bg-slate-800 rounded-md" style={{ width: `${(v / max) * 100}%` }} />
              </div>
              <div className="w-12 text-right text-[12px] font-medium text-slate-800">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Report({ mc }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { if (!mc) return; setRows(null); api.get("/deployment/ageing-report", { params: { master_company_id: mc } }).then(({ data }) => setRows(data.rows || [])).catch(() => setRows([])); }, [mc]);
  useEffect(() => { load(); }, [load]);
  const dl = async () => {
    try {
      const res = await api.get("/deployment/ageing-report", { params: { master_company_id: mc, fmt: "xlsx" }, responseType: "blob" });
      const u = URL.createObjectURL(res.data); const el = document.createElement("a"); el.href = u; el.download = "asset-ageing-report.xlsx"; el.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="space-y-3" data-testid="aac-report">
      <div className="flex justify-between items-center">
        <div className="text-[13px] text-slate-500">{rows ? `${rows.length} assets` : "Loading…"}</div>
        <Button size="sm" variant="outline" onClick={dl} data-testid="aac-report-export"><Download className="h-3.5 w-3.5 mr-1" /> Export XLSX</Button>
      </div>
      <div className="rounded-xl border border-slate-200 overflow-x-auto max-h-[560px]">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>{["Serial", "Product", "Purchase", "Age", "Label", "Condition", "Usage", "Deploys", "Warranty", "Employee"].map((h) => <th key={h} className="text-left p-2 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).slice(0, 400).map((r, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="p-2 font-mono">{r.serial}</td><td className="p-2">{r.product}</td>
                <td className="p-2">{r.purchase_date || "—"}</td><td className="p-2">{r.age}</td>
                <td className="p-2">{r.age_label || "—"}</td><td className="p-2">{r.condition || "—"}</td>
                <td className="p-2">{r.usage}</td><td className="p-2 text-center">{r.previous_deployments}</td>
                <td className="p-2">{r.warranty}</td><td className="p-2">{r.employee || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Coverage({ mc }) {
  const [cov, setCov] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState(null);
  const load = useCallback(() => { if (!mc) return; api.get("/deployment/purchase-coverage", { params: { master_company_id: mc } }).then(({ data }) => setCov(data)).catch(() => {}); }, [mc]);
  useEffect(() => { load(); }, [load]);

  const template = async () => {
    try { const res = await api.get("/deployment/purchase-date/template.xlsx", { responseType: "blob" }); const u = URL.createObjectURL(res.data); const el = document.createElement("a"); el.href = u; el.download = "purchase-date-template.xlsx"; el.click(); URL.revokeObjectURL(u); } catch { toast.error("Download failed"); }
  };
  const doImport = async (dry) => {
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(`/deployment/purchase-date/import?dry_run=${dry}`, fd); setResult(data); if (!dry) { toast.success(`Updated ${data.updated} asset(s)`); load(); } }
    catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  const extract = async (f) => {
    if (!f) return; setBusy(true); setInv(null);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/deployment/invoice/extract", fd); setInv(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Extraction failed"); } finally { setBusy(false); }
  };
  const applyInv = async () => {
    const updates = (inv?.proposals || []).filter((p) => p.matched && p.purchase_date).map((p) => ({ serial: p.serial, purchase_date: p.purchase_date, vendor: p.vendor, invoice_no: p.invoice_no, po_no: p.po_no }));
    if (!updates.length) { toast.info("No matched assets to apply"); return; }
    try { const { data } = await api.post("/deployment/invoice/apply", { updates }); toast.success(`Applied to ${data.updated} asset(s)`); setInv(null); load(); } catch { toast.error("Apply failed"); }
  };

  return (
    <div className="space-y-4" data-testid="aac-coverage">
      {cov && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="aac-coverage-stats">
          {[["Total", cov.total], ["Verified", cov.verified], ["Candidate", cov.candidate], ["Missing", cov.missing]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-slate-900">{v}</div><div className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">{l}</div></div>
          ))}
          <div className="col-span-2 sm:col-span-4 text-[12px] text-slate-500">Coverage: <b className="text-slate-800">{cov.coverage_pct}%</b> of assets have a purchase date.</div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-[13px] font-semibold text-slate-800">Bulk purchase-date import</div>
        <p className="text-[11px] text-slate-500">Upload an XLSX/CSV with columns: serial, purchase_date, vendor, invoice_no, po_no. Matched by serial within this tenant. Dry-run first.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={template} data-testid="aac-pd-template"><Download className="h-3.5 w-3.5 mr-1" /> Template</Button>
          <input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-[12px]" data-testid="aac-pd-file" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => doImport(true)} data-testid="aac-pd-dryrun">Dry run</Button>
          <Button size="sm" disabled={busy} onClick={() => doImport(false)} className="bg-slate-900 text-white" data-testid="aac-pd-commit"><Upload className="h-3.5 w-3.5 mr-1" /> Import</Button>
        </div>
        {result && <div className="text-[12px] text-slate-600" data-testid="aac-pd-result">Matched {result.matched} · Updated {result.updated} · Errors {result.error_count}</div>}
      </div>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
        <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1"><Sparkles className="h-4 w-4 text-emerald-600" /> Invoice AI extraction</div>
        <p className="text-[11px] text-slate-500">Upload an invoice image — ONE AI proposes purchase dates. Review, then apply to matched assets (governed, not auto-verified).</p>
        <input type="file" accept="image/*" onChange={(e) => extract(e.target.files?.[0])} className="text-[12px]" data-testid="aac-invoice-file" />
        {busy && <div className="text-[12px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> Reading…</div>}
        {inv && (
          <div className="space-y-2" data-testid="aac-invoice-proposals">
            <div className="text-[12px] text-slate-700">Extracted: {inv.extracted?.vendor || "—"} · {inv.extracted?.purchase_date || inv.extracted?.invoice_date || "no date"} · {inv.extracted?.invoice_no || "—"}</div>
            <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-[11.5px]"><thead className="bg-slate-50 text-slate-500"><tr>{["Serial", "Product", "Matched", "Purchase date"].map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">{(inv.proposals || []).map((p, i) => <tr key={i}><td className="p-2 font-mono">{p.serial}</td><td className="p-2">{p.product || "—"}</td><td className="p-2">{p.matched ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : "no"}</td><td className="p-2">{p.purchase_date || "—"}</td></tr>)}</tbody>
              </table>
            </div>
            <div className="text-[10px] text-amber-600">{inv.note}</div>
            <Button size="sm" onClick={applyInv} className="bg-emerald-600 text-white" data-testid="aac-invoice-apply">Apply to matched assets</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Policy({ mc }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!mc) return; api.get("/deployment/policy", { params: { master_company_id: mc } }).then(({ data }) => setP(data)).catch(() => {}); }, [mc]);
  if (!p) return <div className="py-10 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>;
  const setThr = (k, v) => setP({ ...p, age_thresholds: { ...p.age_thresholds, [k]: Number(v) } });
  const setMap = (k, v) => setP({ ...p, requirement_map: { ...p.requirement_map, [k]: v } });
  const save = async () => {
    setSaving(true);
    try { await api.put("/deployment/policy", { master_company_id: mc, age_thresholds: p.age_thresholds, requirement_map: p.requirement_map, enforcement: p.enforcement }); toast.success("Allocation policy saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="aac-policy">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-[13px] font-semibold text-slate-800">Age label thresholds (days)</div>
        <p className="text-[11px] text-slate-500">Controls the NEWEST / CURRENT / AGEING / MATURE / REFRESH REVIEW labels shown on device cards.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["newest", "current", "ageing", "mature"].map((k) => (
            <div key={k}><label className="text-[11px] uppercase tracking-wide text-slate-400">{k}</label>
              <Input type="number" value={p.age_thresholds[k]} onChange={(e) => setThr(k, e.target.value)} className="h-9 mt-1" data-testid={`aac-thr-${k}`} /></div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-[13px] font-semibold text-slate-800">Requirement → preference mapping</div>
        <p className="text-[11px] text-slate-500">A soft default only — the tenant always makes the final choice and may override with a reason.</p>
        {REQS.map((r) => (
          <div key={r} className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-slate-600">{r.replace(/_/g, " ")}</span>
            <select value={p.requirement_map[r] || "NO_PREFERENCE"} onChange={(e) => setMap(r, e.target.value)} className="h-8 rounded-lg border border-slate-300 text-[12px] px-2" data-testid={`aac-map-${r}`}>
              {PREF_OPTS.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="text-[12px] text-slate-600">Enforcement</span>
          <select value={p.enforcement} onChange={(e) => setP({ ...p, enforcement: e.target.value })} className="h-8 rounded-lg border border-slate-300 text-[12px] px-2" data-testid="aac-enforcement">
            <option value="SOFT">Soft (preference)</option>
            <option value="MANDATORY">Mandatory (override with reason)</option>
          </select>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-slate-900 text-white" data-testid="aac-policy-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save policy</Button>
    </div>
  );
}
